import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  RootState,
  openMenu,
  openModal,
  setFocus,
} from '../../constants/initialStore';
import { AlbumPage } from '../../types/models/Album';
import styled from 'styled-components';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { AlbumType, albumFormats } from '../../constants/albumFormats';
import { ConvertMMToPixel } from '../../utils/convertCMToPixel';
import useElementDrop from '../../hook/useElementDrop';
import colors from '../../constants/colors';
import { mergeRefs } from 'react-merge-refs';
import { ElementType } from '../../types/models/Elements';
import TextElement from '../../components/cardElements/textElements/TextElement';
import PhotoElement from '../../components/cardElements/photoElements/PhotoElement';
import MotifElement from '../../components/cardElements/motifElements/MotifElement';
import {
  AlbumTitle,
  PageContainer,
  CoverPageContainer,
  SliceContainer,
  SpiraleContainer,
  Title,
} from './AlbumPageStyles';
import { useTranslation } from 'react-i18next';
import ChangeNameModal from '../../components/layout/modals/ChangeNameModal';
import { saveNameAlbum } from '../../api/album';
import GridOverlay from '../../components/functionnalities/GridOverlay';
import { getSliceWidthAndTop, computeTopOffset, isRigideFormat } from '../../utils/slicePositioning';

export const Slice = ({
  id,
  backgroundColor,
  backgroundTheme,
  children,
  numberOfPages,
}: Pick<AlbumPage, 'id' | 'backgroundColor' | 'backgroundTheme'> & {
  children?: ReactNode;
  numberOfPages: number;

}) => {
  const format = useSelector(
    (state: RootState) => state.creation.present.album!.format,
  );
  const ratio = useSelector((state: RootState) => state.ratio!.value);
  const { height } = albumFormats[format];
  const displayCutZone = useSelector((state: RootState) => state.cutZone.value);


  const { widthMM, top } = getSliceWidthAndTop(format, numberOfPages);
  const displayWidth = ConvertMMToPixel(widthMM) * ratio;



  const displayHeight = ConvertMMToPixel(height+36) * ratio;
  const dispatch = useDispatch();

  const element = useSelector((state: RootState) =>
    state.creation.present.elements.value.find(
      (elmt) => elmt.isForSlice === true,
    ),
  );


  const computedElement = element
    ? {
      ...element,
      top: computeTopOffset(format, numberOfPages, ratio),
      left: 0,
      editable: true,
      locked: true,
    }
    : null;


  return (
    <SliceContainer
      $width={displayWidth}
      $height={displayHeight}
      $backgroundColor={backgroundColor ?? ''}
      $backgroundTheme={backgroundTheme}
      $ratio={ratio}
      $displaycutzone={displayCutZone}
    >
      {element && (
        <SliceTextContainer $displayWidth={displayWidth}>
          <TextElement key={element.id} element={computedElement!} index={0} />
        </SliceTextContainer>
      )}
    </SliceContainer>
  );
};

const Spirale = () => {
  const format = useSelector(
    (state: RootState) => state.creation.present.album!.format,
  );
  const ratio = useSelector((state: RootState) => state.ratio!.value);
  const { height } = albumFormats[format];
  const displayWidth = ConvertMMToPixel(20) * ratio;
  const displayHeight = ConvertMMToPixel(height) * ratio;
  const dispatch = useDispatch();

  return (
    <SpiraleContainer
      $width={displayWidth}
      $height={displayHeight}
      $ratio={ratio}
      src={'/spirale.png'}
    />
  );
};

const Page = ({
  id,
  backgroundColor,
  backgroundTheme,
  children,
  title,
  coverID,
  isBackCover = false,
}: Pick<AlbumPage, 'id' | 'backgroundColor' | 'backgroundTheme'> & {
  children?: ReactNode;
  title: string;
  coverID?: string;
  isBackCover?: boolean;
}) => {
  const dispatch = useDispatch();

  const isFocus = useSelector((state: RootState) => state.focus.value === id);
  const format = useSelector(
    (state: RootState) => state.creation.present.album!.format,
  );
  const ratio = useSelector((state: RootState) => state.ratio!.value);
  const { canEditBackCover, width, height } = albumFormats[format];
  //TODO VARIABLE SKU

  const isRigide = isRigideFormat(format);

  const displayWidth = ConvertMMToPixel(width + (isRigide ? 21 : 0)) * ratio;
  const displayHeight = ConvertMMToPixel(height + (isRigide ? 36 : 0)) * ratio;
  const displayCutZone = useSelector((state: RootState) => state.cutZone.value);
  const gridDisplay = useSelector((state: RootState) => state.grid.value);
  // const displayWidth = ConvertMMToPixel(width+21) * ratio;
  // const displayHeight = ConvertMMToPixel(height+36) * ratio;
  // const displayCutZone = useSelector((state: RootState) => state.cutZone.value);
  // const gridDisplay = useSelector((state: RootState) => state.grid.value);

  const elRef = useRef(null);

  const pageElements = useSelector(
    (state: RootState) =>
      state.creation.present.elements.value.filter((el) => el.pageID === id),
    { equalityFn: shallowEqual },
  );

  const drop = useElementDrop({ pageID: id });

  const onClick = () => {
    dispatch(setFocus(!canEditBackCover && coverID ? coverID : id));
    dispatch(openMenu(4));
  };

  return (
    <CoverContainer>
      <PageBorder $active={isFocus}>
        <CoverPageContainer
          $width={displayWidth}
          $height={displayHeight}
          $backgroundColor={backgroundColor ?? ''}
          $backgroundTheme={backgroundTheme}
          $displaycutzone={displayCutZone}
          $ratio={ratio}
          ref={mergeRefs([elRef, drop])}
          onClick={onClick}
          $isSlice={!isBackCover}
          $borderSize={isRigide ? 10 : 1.5}
        >
          {gridDisplay && <GridOverlay />}
          {children}
          {pageElements.map((element, index) => {
            switch (element.type) {
              case ElementType.TEXT:
                if (!element.isForSlice) {
                  return (
                    <TextElement
                      key={element.id}
                      element={element}
                      index={index}
                    />
                  );
                } else {
                  return null;
                }
              case ElementType.PHOTO:
                return (
                  <PhotoElement
                    key={element.id}
                    element={element}
                    index={index}
                  />
                );
              case ElementType.MOTIF:
                return (
                  <MotifElement
                    key={element.id}
                    element={element}
                    index={index}
                  />
                );
            }
          })}
        </CoverPageContainer>
      </PageBorder>
      <Title>{title}</Title>
    </CoverContainer>
  );
};

const CoverDisplayer = ({
  cover,
  backCover,
  $viewPerPage,
  $isAtTheEnd,
}: {
  cover: AlbumPage;
  backCover: AlbumPage;
  $viewPerPage: boolean;
  $isAtTheEnd?: boolean;
}) => {
  const [changeNameModalVisible, setChangeNameModalVisible] = useState(false);
  const dispatch = useDispatch();

  const creation = useSelector(
    (state: RootState) => state.creation.present.album,
  );
  const name = useSelector(
    (state: RootState) => state.creation.present.album!.name,
  );
  const type = useSelector(
    (state: RootState) =>
      albumFormats[state.creation.present.album!.format].type,
  );
  const format = useSelector(
    (state: RootState) => state.creation.present.album!.format,
  );
  const albumID = useSelector(
    (state: RootState) => state.creation.present.album!.id,
  );

  const handleChangeName = async (newName: string) => {
    if (albumID) {
      await saveNameAlbum(albumID, newName);
    }
    setChangeNameModalVisible(false);
  };

  const onClickCover = () => {
    dispatch(setFocus(cover.id));
    dispatch(openMenu(4));
  };

  const onClickBackCover = () => {
    dispatch(setFocus(backCover.id));
    dispatch(openMenu(4));
  };

  const { t } = useTranslation(['common']);
  const numberOfPages = (creation?.albumPages.length ?? 0) + 2;

  return (
    <AlbumPageContainer
      id={
        $viewPerPage && $isAtTheEnd
          ? 'creationElementPage-' + creation?.albumPages.length
          : 'creationElement--1'
      }
      $hasSpiral={type === AlbumType.SPIRALE}
      $viewPerPage={$viewPerPage}
    >
      <ChangeNameModal
        visible={changeNameModalVisible}
        setVisible={setChangeNameModalVisible}
        initialName={name ?? ''}
        handleValidate={handleChangeName}
      />
      {(!$viewPerPage || $isAtTheEnd) && (
        <Page
          backgroundColor={cover.backgroundColor}
          backgroundTheme={cover.backgroundTheme}
          id={backCover.id}
          title={t('album.backCover')}
          coverID={cover.id}
          isBackCover={true}
        />
      )}
      {type === AlbumType.RIGIDE && !$isAtTheEnd && (
        <Slice {...cover} numberOfPages={numberOfPages} />
      )}      {type === AlbumType.SPIRALE && !$viewPerPage && <Spirale />}
      {(!$viewPerPage || !$isAtTheEnd) && (
        <Page {...cover} title={t('album.cover')} isBackCover={false} />
      )}
    </AlbumPageContainer>
  );
};

const AlbumPageContainer = styled.div<{
  $hasSpiral: boolean;
  $viewPerPage: boolean;
}>`
  margin-top: 70px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.$hasSpiral || props.$viewPerPage ? '2px' : '12px')};
`;

const CoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const PageBorder = styled.div<{ $active: boolean }>`
  border: solid 1px
    ${({ $active }) => ($active ? colors.gray400 : 'transparent')};
`;


const SliceTextContainer = styled.div<{ $displayWidth: number }>`
  transform: translateY(${(props) => props.$displayWidth}px) rotate(90deg);
  transform-origin: 100% 0%;
  & > div {
    & > div {
      align-content: center;
    }
  }
`;

export default CoverDisplayer;
